<template>
  <div class="card shadow-sm h-100">
    <div class="card-body">
      <div class="chart-wrapper" style="height: 420px;">
        <canvas :id="chartId" :key="chartKey"></canvas>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import Chart from 'chart.js/auto'

const props = defineProps({
  surveyReport: { type: Object, required: true },
  selectedCategory: { type: String, required: true },
  selectedPopulation: { type: String, required: true },
  title: { type: String, default: 'Radar (1er niveau)' }
})

const chart = ref(null)
const chartId = computed(() => `radar-overview-first-${props.selectedCategory}`)
const chartKey = computed(() => `${props.selectedCategory}-${props.selectedPopulation}-first`)

const populationGroups = computed(() => Object.keys(props.surveyReport || {}).filter(k => k !== 'all_populations'))

const getNodeAverage = (node) => {
  if (!node) return 0
  if (typeof node.average_percent === 'number') return node.average_percent
  const children = node.sub_categories || {}
  const values = Object.values(children).map(c => (typeof c?.average_percent === 'number' ? c.average_percent : 0))
  if (!values.length) return 0
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length)
}

const getFirstLevelLabels = (root) => {
  const firstLevel = root?.types?.[props.selectedCategory]?.sub_categories || {}
  return Object.keys(firstLevel)
}

const getUnionFirstLevelLabelsAcrossGroups = () => {
  const set = new Set()
  populationGroups.value.forEach(groupId => {
    const root = props.surveyReport[groupId]
    getFirstLevelLabels(root).forEach(l => set.add(l))
  })
  return Array.from(set)
}

const getDatasetForGroupFirst = (groupId, labels) => {
  const root = groupId === 'all_populations'
    ? (props.surveyReport.all_populations || props.surveyReport)
    : props.surveyReport[groupId]
  const firstLevel = root?.types?.[props.selectedCategory]?.sub_categories || {}
  return labels.map(label => getNodeAverage(firstLevel[label]))
}

const getChartData = () => {
  if (!props.surveyReport) return { labels: [], datasets: [] }
  const aggregatedRoot = (props.surveyReport.all_populations || props.surveyReport)
  let labels
  if (props.selectedPopulation === 'true') {
    labels = getUnionFirstLevelLabelsAcrossGroups()
  } else {
    labels = getFirstLevelLabels(aggregatedRoot)
  }
  if (!labels.length) return { labels: [], datasets: [] }
  const datasets = []
  if (props.selectedPopulation === 'true') {
    populationGroups.value.forEach(groupId => {
      const pop = props.surveyReport[groupId]

      console.log("pop",pop)
      const data = getDatasetForGroupFirst(groupId, labels)
      const color = pop?.color || '#36a2eb'
      datasets.push({
        label: `${pop?.name} (${groupId})`,
        data,
        backgroundColor: color,
        borderColor: color,
        fill: false,
        borderWidth: 2,
        pointBackgroundColor: color
      })
    })
  } else {
    const data = getDatasetForGroupFirst('all_populations', labels)
    datasets.push({ label: 'Toutes populations', data, backgroundColor: '#36a2eb', borderColor: '#36a2eb', fill: false, borderWidth: 2, pointBackgroundColor: '#36a2eb' })
  }
  return { labels, datasets }
}

const options = {
  responsive: true,
  maintainAspectRatio: false,
  scales: { r: { suggestedMin: 0, suggestedMax: 100, ticks: { stepSize: 20, backdropColor: 'transparent' } } },
  plugins: { legend: { position: 'right' } }
}

const destroyChart = () => { if (chart.value) { try { chart.value.destroy() } catch (e) {} chart.value = null } }
const init = () => {
  destroyChart()
  nextTick(() => {
    const canvas = document.getElementById(chartId.value)
    if (!canvas) return
    try { const existing = Chart.getChart(canvas); if (existing) existing.destroy() } catch (e) {}
    const data = getChartData()
    if (!data.labels.length || !data.datasets.length) return
    try { chart.value = new Chart(canvas.getContext('2d'), { type: 'radar', data, options }) } catch (e) { console.error(e) }
  })
}

watch([() => props.surveyReport, () => props.selectedCategory, () => props.selectedPopulation], () => { init() }, { immediate: true, deep: true })
onUnmounted(() => { destroyChart() })
</script>

<style scoped>
.chart-wrapper { position: relative; width: 70%;margin-left: 300px }
</style>


